// Tiny WebAudio synth — no asset files, no network. Each call builds
// a short oscillator chain on demand and lets it decay. Designed so
// the only side-effect is sound; if AudioContext is unavailable or the
// user has muted, calls become no-ops.
//
// Mute is persisted in localStorage so the choice survives reloads.
const MUTE_KEY = "hl.skillSounds.muted.v1";

export function isSkillSoundsMuted(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSkillSoundsMuted(muted: boolean) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    window.dispatchEvent(new CustomEvent("hl:skillSounds:change"));
  } catch {
    /* private mode — ignore */
  }
}

let ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

function blip(freq: number, startOffset: number, durMs: number, gain = 0.06) {
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime + startOffset;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durMs / 1000);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + durMs / 1000 + 0.02);
}

/** Three rising notes — generic "skill done" jingle (~350ms total). */
export function playSkillComplete() {
  if (isSkillSoundsMuted()) return;
  blip(523.25, 0, 120); // C5
  blip(659.25, 0.09, 120); // E5
  blip(783.99, 0.18, 180, 0.07); // G5
}

/** Bigger fanfare — 5 rising notes, used on milestone (every 5 skills). */
export function playMilestone() {
  if (isSkillSoundsMuted()) return;
  const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
  notes.forEach((f, i) => blip(f, i * 0.07, 220, 0.08));
}

/** Short coin chirp — daily bonus claim. */
export function playCoin() {
  if (isSkillSoundsMuted()) return;
  blip(987.77, 0, 90, 0.07);
  blip(1318.5, 0.06, 140, 0.07);
}
